// ModelDocumentsSection — the product peek's Documents section (ADR-068,
// FRONTEND-PATTERNS §Files: "the product peek — documents as dense FileRows in a
// section; a local model's Manage opens the documents face in a SlideOver").
//
// Two tiers, one section:
//   CANONICAL — the record's artifacts (sha-addressed catalog blobs, ADR-068 §1:
//   no files rows): an available one is a real row — open (pdf/image, the files
//   origin) + download with its title; a missing one keeps the honest "not yet
//   imported" line. The evidence CITATIONS render after them (a citation is a
//   different fact — it stays when the file arrives).
//   LOCAL — the model's own paper through the files module (always filed): rows
//   from the shelf read; the card's Manage (the product's registry —
//   `AssetModelActions.tsx`, asset.models:manage) opens the SlideOver face.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssetModelProjectItem, CatalogArtifactItem } from '@ems-app/schemas/catalog/responses';
import type { LibraryFile as WireFile } from '@ems-app/schemas/files/responses';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { SectionAct } from '../../components/patterns/SectionAct.js';
import { sectionActOf, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { FamilyGlyph, FileRow, displayName, shortFileName, type LibraryFile } from '../../components/patterns/filesDisplay.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { icons } from '../../components/ui/icons.js';
import { useToast } from '../../components/ui/Toast.js';
import { downloadCatalogArtifact, downloadFile, useShelf } from '../../hooks/useFiles.js';
import { catalogFileId } from '../../lib/catalogFileId.js';
import { formatBytes } from '../../lib/format.js';
import { ModelFileSurface, artifactFilename, catalogLibraryFile, familyOfMime } from './ModelFileSurface.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

function ArtifactRow({ projectId, artifact, onOpen }: { projectId: string; artifact: CatalogArtifactItem; onOpen: () => void }) {
  const { t } = useTranslation('catalog');
  const { t: tf } = useTranslation('files');
  const toast = useToast();
  const family = familyOfMime(artifact.mime);
  const kind = t(`peek.artifactKind.${artifact.kind}`, { defaultValue: t('peek.artifactKind.other') });
  const subline = [kind, artifact.rev, formatBytes(artifact.size)].filter(Boolean).join(' · ');
  const fail = useRefusalToast('model-documents');
  return (
    <li className="flex items-center gap-2 py-1.5">
      <span className={artifact.available ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-tertiary)]'}>
        <FamilyGlyph family={family} contentType={artifact.mime} />
      </span>
      <span className="min-w-0 flex-1">
        {artifact.available ? (
          <button type="button" dir="ltr" onClick={onOpen} className="block max-w-full truncate text-start text-sm text-[var(--color-text-primary)] underline-offset-2 hover:underline">
            {artifact.title}
          </button>
        ) : (
          <span dir="ltr" className={`block max-w-full truncate text-sm ${artifact.available ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>{artifact.title}</span>
        )}
        <span className="block truncate text-xs text-[var(--color-text-secondary)]">
          {subline}
          {!artifact.available && <> · {t('peek.docPending')}</>}
        </span>
      </span>
      {artifact.available && (
        <IconButton
          variant="ghost"
          aria-label={tf('actions.downloadNamed', { name: shortFileName(artifact.title) })}
          onClick={() => {
            toast.info(tf('toast.downloading', { name: shortFileName(artifact.title) }));
            downloadCatalogArtifact(projectId, artifact.sha256, artifactFilename(artifact)).catch(fail);
          }}
        >
          <icons.download size={18} aria-hidden />
        </IconButton>
      )}
    </li>
  );
}

export function ModelDocumentsSection({ projectId, m, acts }: { projectId: string; m: AssetModelProjectItem; acts: readonly EntityAct[] }) {
  const { t } = useTranslation('catalog');
  const { t: tf } = useTranslation('files');
  const toast = useToast();
  const local = !m.is_canonical && !m.is_generic;
  const anchor = `model:${m.id}`;
  // The model's own paper — locals only (a canonical's documents are the
  // catalog's artifacts; its upload door is closed, so the read would be empty).
  const shelf = useShelf(local ? projectId : '', { anchor });
  const files = useMemo<LibraryFile[]>(() => (shelf.data?.documents ?? []).map((f: WireFile) => ({ ...f, thumb: null, fresh: false })), [shelf.data]);
  const artifacts = useMemo(() => m.artifacts ?? [], [m.artifacts]);
  const citations = m.evidence ?? [];
  const fail = useRefusalToast('model-documents');
  // The lens the surface's pager walks — the rows in render order (the local
  // paper, then the catalog's; only openable artifacts enter the walk).
  const artifactFiles = useMemo(() => artifacts.filter((a) => a.available).map((a) => catalogLibraryFile(a, m)), [artifacts, m]);
  const lens = useMemo(() => [...files, ...artifactFiles], [files, artifactFiles]);
  const [openId, setOpenId] = useState<string | null>(null);
  const empty = artifacts.length === 0 && citations.length === 0 && files.length === 0;
  // An empty local section stays only for the one who can fill it (its Manage).
  if (!m.is_canonical && empty && !sectionActOf(acts, 'documents')) return null;
  return (
    <>
      <DetailSection
        dense
        title={t('peek.sections.documents')}
        action={<SectionAct acts={acts} section="documents" />}
      >
        <div className="flex flex-col gap-1.5">
          {files.length > 0 && (
            <div className="-mx-3 flex flex-col">
              {files.map((f) => (
                <FileRow
                  key={f.id}
                  file={f}
                  onSelect={(x) => setOpenId(x.id)}
                  chipLabel={f.folder ? tf(`folders.${f.folder}`, { defaultValue: f.folder }) : undefined}
                  quickActions={
                    <IconButton
                      variant="ghost"
                      aria-label={tf('actions.downloadNamed', { name: shortFileName(displayName(f)) })}
                      onClick={() => {
                        toast.info(tf('toast.downloading', { name: shortFileName(displayName(f)) }));
                        downloadFile(projectId, f).catch(fail);
                      }}
                    >
                      <icons.download size={18} aria-hidden />
                    </IconButton>
                  }
                />
              ))}
            </div>
          )}
          {artifacts.length > 0 && (
            <ul className="flex flex-col">
              {artifacts.map((a) => (
                <ArtifactRow key={a.sha256} projectId={projectId} artifact={a} onOpen={() => setOpenId(catalogFileId(a.sha256, m.id))} />
              ))}
            </ul>
          )}
          {citations.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {citations.map((e) => (
                <li key={e} className="text-sm">
                  <span className="flex items-start gap-1.5">
                    <icons.file size={15} aria-hidden className="mt-0.5 shrink-0 text-[var(--color-text-secondary)]" />
                    <span className="min-w-0">
                      <span dir="ltr" className="text-[var(--color-text-primary)]">{e}</span>
                      {/* A citation is a citation, never a file: nothing waits on a pack for it. "Not yet
                          imported" is the ARTIFACT row's line (above) — on a citation it read as a missing
                          file and sent the operator re-uploading packs that change nothing. */}
                      <span className="block text-xs text-[var(--color-text-secondary)]">{t('peek.citationOnly')}</span>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {empty && <p className="text-sm text-[var(--color-text-secondary)]">{t('peek.docsNone')}</p>}
        </div>
      </DetailSection>
      {openId && <ModelFileSurface projectId={projectId} model={m} items={lens} openId={openId} onOpen={setOpenId} />}
    </>
  );
}
